import React from 'react';
import {FmlxIcon} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {getSessionUser} from 'stores/selectors/sessionSelectors';
import {ProtocolExplorerAccordionGroup} from 'constants/protocolExplorerEnum';
import ProtocolTooltipWrapper from './ProtocolTooltipWrapper';
import {DoubleClickDiv} from '../../components';
import {PROTOCOL_NAME_EXTENSIONS} from '../../constants/dataEnum';
import ProtocolStatus from './ProtocolStatus';
import createSelectableItem from '../../components/SelectableArea/createSelectableItem';
import useDragDropProtocol from './useDragDropProtocol';
import {
  EXPLORER_ITEM_CLASSNAME,
  getProtocolIcon,
  isProtocolOwner,
  protocolExplorerHelper,
} from '../../lib/explorerHelper';
import ProtocolExplorerFavoriteIndicator from './ProtocolExplorerFavoriteIndicator';

const SingleProtocol = createSelectableItem((props) => {
  const {
    protocol,
    showFileExtension,
    selectableRef,
    isRunning,
    onClick,
    onDoubleClick,
    greyedOut,
    onBeginDrag,
    onEndDrag,
    onDropped,
    isDragMode,
    data,
    isOperatorRoleLoggedIn,
  } = props;
  const {userId} = useSelector(getSessionUser);
  const protocolOwner = isProtocolOwner(protocol, isOperatorRoleLoggedIn, userId);
  const isDraggable = protocolExplorerHelper.checkIsItemDraggable(protocol, userId);
  const isShadowFavorite = protocol.groupView !== ProtocolExplorerAccordionGroup.FAVORITE_PROTOCOLS;

  function handleClick() {
    onClick(protocol);
  }

  function handleDoubleClick() {
    onDoubleClick(protocol);
  }

  let name = protocol.name ? protocol.name : '';

  if (!showFileExtension) {
    name = name.substr(0, name.length - PROTOCOL_NAME_EXTENSIONS.length);
  }

  const {drag, drop, isDragOver} = useDragDropProtocol({
    protocol,
    onBeginDrag,
    onEndDrag,
    onDropped,
    isRunning,
    disableDrag: isOperatorRoleLoggedIn || !isDraggable,
  });

  return (
    <div
      data-id={data}
      name={protocol.name}
      className={[
        'single-protocol-container',
        EXPLORER_ITEM_CLASSNAME,
        `${protocol.isSelected ? 'selected' : ''}`,
        `${protocol.isSelected && isDragMode ? 'dragged' : ''}`,
        `${isDragOver ? 'drag-over' : ''}`,
      ].join(' ')}
      ref={(e) => {
        selectableRef(e);
        drag(e);
        drop(e);
      }}
    >
      <DoubleClickDiv key={protocol.ID} className="protocol" onClick={handleClick} onDoubleClick={handleDoubleClick}>
        <ProtocolTooltipWrapper item={protocol}>
          <div className="protocol-icon">
            <div className="protocol-icon-wrapper">
              <FmlxIcon
                name={getProtocolIcon(protocol, isOperatorRoleLoggedIn, protocolOwner)}
                size="lg"
                customColor={greyedOut ? '#B4B4B4' : '#414141'}
              />
              <ProtocolStatus protocol={protocol} isRunning={isRunning} />
            </div>
          </div>
          <div className="protocol-name-wrapper">
            {protocol.isFavorite && <ProtocolExplorerFavoriteIndicator greyedOut={greyedOut || isShadowFavorite} />}
            <span className={`protocol-name${greyedOut ? ' greyed-out' : ''}`}>{name}</span>
          </div>
        </ProtocolTooltipWrapper>
      </DoubleClickDiv>
    </div>
  );
});

SingleProtocol.propTypes = {
  protocol: PropTypes.any.isRequired,
  isRunning: PropTypes.bool.isRequired,
  showFileExtension: PropTypes.bool.isRequired,
  greyedOut: PropTypes.bool,
  isOperatorRoleLoggedIn: PropTypes.bool.isRequired,
};

export default SingleProtocol;
